import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { extractErr, mediaUrl } from "@/lib/api";
import { useFileUpload } from "@/lib/uploadProgress";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Smartphone, Hammer, Download, Loader2, UploadCloud, CheckCircle2,
  XCircle, Clock, Palette, RefreshCw, Lock, LifeBuoy, ShieldCheck,
  Images, Trash2,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ACTIVE = ["queued", "building"];

const STAGE_LABEL = {
  queued: "Waiting in queue", waiting_for_toolchain: "Preparing build environment",
  scaffold: "Creating project", configure: "Applying your branding",
  dependencies: "Fetching dependencies", icons: "Generating app icon",
  signing: "Creating signing key", building_apk: "Building APK", building_aab: "Building AAB (Play Store)",
  packaging: "Packaging", done: "Done",
};

function StatusBadge({ status }) {
  if (status === "success") return <Badge className="bg-green-100 text-green-700 hover:bg-green-100"><CheckCircle2 className="w-3 h-3 mr-1" />Ready</Badge>;
  if (status === "failed") return <Badge className="bg-red-100 text-red-700 hover:bg-red-100"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>;
  if (status === "building") return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100"><Loader2 className="w-3 h-3 mr-1 animate-spin" />Building</Badge>;
  return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100"><Clock className="w-3 h-3 mr-1" />Queued</Badge>;
}

export default function AppBuilder() {
  const uploadFile = useFileUpload();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [building, setBuilding] = useState(false);
  const [form, setForm] = useState({
    app_name: "", package_id: "", primary_color: "#2563EB",
    whatsapp_checkout: false, version_name: "1.0.0", version_code: 1, delivery_app: false,
  });
  const [iconB64, setIconB64] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [splashB64, setSplashB64] = useState(null);
  const [splashPreview, setSplashPreview] = useState(null);
  const fileRef = useRef();
  const splashRef = useRef();

  // --- Home sliders (runtime-controlled banners) ---
  const [sliders, setSliders] = useState([]);
  const [cats, setCats] = useState([]);
  const [prods, setProds] = useState([]);
  const [sliderSaving, setSliderSaving] = useState(false);
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const slideRef0 = useRef();
  const slideRef1 = useRef();
  const slideRef2 = useRef();
  const slideRefs = [slideRef0, slideRef1, slideRef2];

  const absUrl = (u) => (u && u.startsWith("/") ? `${BACKEND}${u}` : u);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/merchant/app-builder");
      setData(data);
      setSliders(Array.isArray(data.sliders) ? data.sliders : []);
      const c = data.config || {};
      setForm((f) => ({
        app_name: c.app_name ?? f.app_name,
        package_id: c.package_id ?? f.package_id,
        primary_color: c.primary_color ?? f.primary_color,
        whatsapp_checkout: c.whatsapp_checkout ?? f.whatsapp_checkout,
        delivery_app: Boolean(data.ondemand_theme),
        version_name: c.version_name ?? f.version_name,
        version_code: c.version_code ?? f.version_code,
      }));
      if (c.splash_b64) setSplashPreview(c.splash_b64);
      if (c.icon_b64) setIconPreview(c.icon_b64);
      if (c.package_id === undefined && !c.app_name) {
        // suggest a package id from store slug
        setForm((f) => ({ ...f, package_id: f.package_id || `com.merastartup.${(data.store_slug || "store").replace(/[^a-z0-9]/g, "")}` }));
      }
    } catch (e) {
      toast.error(extractErr(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Load categories + products for the slider link picker
  useEffect(() => {
    (async () => {
      try {
        const [c, p] = await Promise.all([
          api.get("/merchant/categories"),
          api.get("/merchant/products"),
        ]);
        setCats(c.data || []);
        setProds((p.data || []).slice(0, 300));
      } catch { /* non-critical */ }
    })();
  }, []);

  // slider helpers
  const onSliderFile = async (idx, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB"); return; }
    setUploadingIdx(idx);
    try {
      const fd = new FormData();
      fd.append("kind", "slider");
      fd.append("file", file);
      const res = await uploadFile("/merchant/media/upload", fd);
      const url = absUrl(res.url);
      setSliders((prev) => {
        const next = [...prev];
        next[idx] = { image_url: url, link_type: next[idx]?.link_type || "none", link_value: next[idx]?.link_value || "" };
        return next;
      });
      toast.success("Banner uploaded");
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setUploadingIdx(null);
      if (e.target) e.target.value = "";
    }
  };

  const setSliderField = (idx, patch) => {
    setSliders((prev) => {
      const next = [...prev];
      next[idx] = { ...(next[idx] || { image_url: "", link_type: "none", link_value: "" }), ...patch };
      return next;
    });
  };

  const removeSlider = (idx) => setSliders((prev) => prev.filter((_, i) => i !== idx));

  const addSliderSlot = () => setSliders((prev) => (prev.length >= 3 ? prev : [...prev, { image_url: "", link_type: "none", link_value: "" }]));

  const saveSliders = async () => {
    setSliderSaving(true);
    try {
      const payload = sliders
        .filter((s) => s && s.image_url)
        .map((s) => ({
          image_url: s.image_url,
          link_type: s.link_type || "none",
          link_value: (s.link_type && s.link_type !== "none") ? (s.link_value || "") : "",
        }));
      const { data: res } = await api.put("/merchant/app-builder/sliders", { sliders: payload });
      setSliders(res.sliders || []);
      toast.success("Sliders saved — they update in the app instantly");
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setSliderSaving(false);
    }
  };

  // poll while a build is active
  const activeBuild = (data?.builds || []).find((b) => ACTIVE.includes(b.status));
  useEffect(() => {
    if (!activeBuild) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [activeBuild, load]);

  const onIcon = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Icon must be under 2MB"); return; }
    const reader = new FileReader();
    reader.onload = () => { setIconB64(reader.result); setIconPreview(reader.result); };
    reader.readAsDataURL(file);
  };

  const onSplash = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("Splash image must be under 3MB"); return; }
    const reader = new FileReader();
    reader.onload = () => { setSplashB64(reader.result); setSplashPreview(reader.result); };
    reader.readAsDataURL(file);
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const body = { ...form, version_code: Number(form.version_code) || 1, delivery_app: Boolean(data.ondemand_theme) };
      if (iconB64) body.icon_b64 = iconB64;
      if (splashB64) body.splash_b64 = splashB64;
      const { data: res } = await api.put("/merchant/app-builder/config", body);
      toast.success("App configuration saved");
      setIconB64(null);
      setSplashB64(null);
      await load();
      return res;
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setSaving(false);
    }
  };

  const startBuild = async () => {
    setBuilding(true);
    try {
      await api.post("/merchant/app-builder/build");
      toast.success("Build started — this can take a few minutes");
      await load();
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setBuilding(false);
    }
  };

  if (!data) {
    return <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading builder…</div>;
  }

  const pf = data.payment_flags || {};
  const methods = [
    pf.cod_enabled && "Cash on Delivery",
    (pf.razorpay_enabled || pf.cashfree_enabled || pf.phonepe_enabled || pf.stripe_enabled || pf.paypal_enabled) && "Pay Online",
    form.whatsapp_checkout && "WhatsApp",
  ].filter(Boolean);

  const gate = data.build_gate || { can_build: true, status: "ok" };
  const latestBuild = (data.builds || [])[0];
  const lastFailed = latestBuild?.status === "failed" && !activeBuild;
  const supportBtn = (
    <Link to="/admin/support" data-testid="builder-support-btn">
      <Button size="sm" variant="outline" className="border-slate-300">
        <LifeBuoy className="w-4 h-4 mr-1.5" /> Contact support
      </Button>
    </Link>
  );
  const fmtWhen = (v) => { try { return new Date(v).toLocaleString(); } catch { return ""; } };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Config form */}
        <Card className="p-6 space-y-4" data-testid="builder-config">
          <div className="flex items-center gap-2 font-display font-bold text-lg text-slate-900">
            <Palette className="w-5 h-5 text-blue-600" /> App configuration
          </div>

          <div className="space-y-1">
            <Label>App name</Label>
            <Input data-testid="builder-app-name" value={form.app_name}
              onChange={(e) => setForm({ ...form, app_name: e.target.value })} placeholder="Fresh Fruits" />
          </div>

          <div className="space-y-1">
            <Label>Android package id</Label>
            <Input data-testid="builder-package-id" value={form.package_id}
              onChange={(e) => setForm({ ...form, package_id: e.target.value })} placeholder="com.brand.shop" />
            <p className="text-xs text-slate-400">Reverse-domain, all lowercase. Cannot be changed after publishing.</p>
          </div>

          <div className="flex gap-4">
            <div className="space-y-1">
              <Label>Brand colour</Label>
              <div className="flex items-center gap-2">
                <input type="color" data-testid="builder-color" value={form.primary_color}
                  onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                  className="w-11 h-10 rounded border cursor-pointer" />
                <Input value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="w-28" />
              </div>
            </div>
            <div className="space-y-1 flex-1">
              <Label>App icon</Label>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl border overflow-hidden bg-slate-50 flex items-center justify-center">
                  {iconPreview ? <img src={iconPreview} alt="icon" className="w-full h-full object-cover" />
                    : <Smartphone className="w-5 h-5 text-slate-400" />}
                </div>
                <input ref={fileRef} type="file" accept="image/png,image/jpeg" onChange={onIcon} className="hidden" data-testid="builder-icon-input" />
                <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                  <UploadCloud className="w-4 h-4 mr-1" /> Upload
                </Button>
              </div>
              <p className="text-xs text-slate-400">Square PNG, 512×512 recommended.</p>
            </div>
          </div>

          <div className="space-y-1">
            <Label>Splash screen background</Label>
            <div className="flex items-center gap-3">
              <div className="w-16 h-24 rounded-xl border overflow-hidden bg-slate-50 flex items-center justify-center">
                {splashPreview ? <img src={splashPreview} alt="splash" className="w-full h-full object-cover" />
                  : <Images className="w-5 h-5 text-slate-400" />}
              </div>
              <input ref={splashRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onSplash} className="hidden" data-testid="builder-splash-input" />
              <Button type="button" variant="outline" size="sm" onClick={() => splashRef.current?.click()}>
                <UploadCloud className="w-4 h-4 mr-1" /> Upload splash
              </Button>
            </div>
            <p className="text-xs text-slate-400">Portrait image (1080×1920 recommended). Baked in when you generate the Android app.</p>
          </div>

          <div className="flex gap-4">
            <div className="space-y-1">
              <Label>Version name</Label>
              <Input value={form.version_name} onChange={(e) => setForm({ ...form, version_name: e.target.value })} className="w-32" />
            </div>
            <div className="space-y-1">
              <Label>Version code</Label>
              <Input type="number" value={form.version_code} onChange={(e) => setForm({ ...form, version_code: e.target.value })} className="w-28" />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg bg-slate-50 p-3">
            <div>
              <div className="text-sm font-medium text-slate-800">WhatsApp checkout</div>
              <div className="text-xs text-slate-500">Let customers place orders via your WhatsApp</div>
            </div>
            <Switch checked={form.whatsapp_checkout} onCheckedChange={(v) => setForm({ ...form, whatsapp_checkout: v })} data-testid="builder-whatsapp" />
          </div>

          {data.ondemand_theme ? (
            <div className="flex items-center justify-between rounded-lg bg-slate-50 p-3" data-testid="builder-delivery-row">
              <div>
                <div className="text-sm font-medium text-slate-800">Delivery app</div>
                <div className="text-xs text-slate-500">Required for the Ondemand theme: login first, pincode header, and tracking screens. This stays on while Ondemand is active.</div>
              </div>
              <Switch checked disabled data-testid="builder-delivery-app" />
            </div>
          ) : null}

          <Button onClick={saveConfig} disabled={saving} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="builder-save">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : "Save configuration"}
          </Button>
        </Card>

        {/* Build panel */}
        <div className="space-y-6">
          <Card className="p-6 space-y-4" data-testid="builder-build-panel">
            <div className="flex items-center gap-2 font-display font-bold text-lg text-slate-900">
              <Hammer className="w-5 h-5 text-blue-600" /> Build your app
            </div>
            <p className="text-sm text-slate-500">
              We build a real, Play-Store-ready signed <b>APK</b> and <b>AAB</b> on our servers.
              Builds run one at a time, so you may wait in a short queue.
            </p>
            <div className="text-xs text-slate-500">
              Payment methods in the app: {methods.length ? methods.join(", ") : "None enabled yet — set them in Settings → Payments"}
            </div>
            {!data.toolchain_ready && (
              <div className="text-xs text-amber-600 flex items-center gap-1" data-testid="builder-toolchain">
                <Loader2 className="w-3 h-3 animate-spin" /> Build environment is warming up…
              </div>
            )}

            {/* App is live — admin locked the builder */}
            {gate.status === "locked_admin" && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="builder-gate-locked">
                <div className="flex items-center gap-2 font-semibold text-slate-800"><Lock className="w-4 h-4" /> App building is locked</div>
                <p className="mt-1 text-sm text-slate-600">{gate.message}</p>
                <div className="mt-3">{supportBtn}</div>
              </div>
            )}

            {/* 72h cooldown after a successful build */}
            {gate.status === "cooldown" && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="builder-gate-cooldown">
                <div className="flex items-center gap-2 font-semibold text-emerald-800"><CheckCircle2 className="w-4 h-4" /> Your app was built successfully</div>
                <p className="mt-1 text-sm text-emerald-700">{gate.message}</p>
                {gate.next_build_at && <p className="mt-1 text-xs text-emerald-600">You can build again after <b>{fmtWhen(gate.next_build_at)}</b>. Download your APK/AAB below in the meantime.</p>}
              </div>
            )}

            {/* Admin manually unlocked */}
            {gate.status === "unlocked_admin" && (
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700 flex items-center gap-2" data-testid="builder-gate-unlocked">
                <ShieldCheck className="w-4 h-4" /> {gate.message}
              </div>
            )}

            {/* Last build failed — keep open + offer support */}
            {lastFailed && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-4" data-testid="builder-gate-failed">
                <div className="flex items-center gap-2 font-semibold text-red-800"><XCircle className="w-4 h-4" /> Your last build failed</div>
                <p className="mt-1 text-sm text-red-700">You can try building again. If it keeps failing, raise a support ticket and our team will help.</p>
                {latestBuild?.error ? <p className="mt-1 text-xs text-red-700 font-mono">{latestBuild.error}</p> : null}
                {latestBuild?.log_tail ? (
                  <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap rounded bg-red-100/70 p-2 text-[10px] text-red-800 font-mono">{latestBuild.log_tail}</pre>
                ) : null}
                <div className="mt-3">{supportBtn}</div>
              </div>
            )}

            <Button onClick={startBuild} disabled={building || !!activeBuild || !data.has_config || !gate.can_build}
              className="w-full bg-blue-600 hover:bg-blue-700" data-testid="builder-build-btn">
              {building ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Starting…</>
                : activeBuild ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Build in progress…</>
                : gate.status === "locked_admin" ? <><Lock className="w-4 h-4 mr-2" />Locked by admin</>
                : gate.status === "cooldown" ? <><Clock className="w-4 h-4 mr-2" />Available after {fmtWhen(gate.next_build_at)}</>
                : <><Hammer className="w-4 h-4 mr-2" />Build APK & AAB</>}
            </Button>
            {!data.has_config && <p className="text-xs text-amber-600">Save your app configuration before building.</p>}
          </Card>

          <Card className="p-6" data-testid="builder-history">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display font-bold text-slate-900">Builds</div>
              <button onClick={load} className="text-slate-400 hover:text-slate-600"><RefreshCw className="w-4 h-4" /></button>
            </div>
            {(data.builds || []).length === 0 ? (
              <p className="text-sm text-slate-400">No builds yet.</p>
            ) : (
              <div className="space-y-3">
                {data.builds.map((b) => (
                  <div key={b.id} className="border rounded-lg p-3" data-testid="builder-build-row">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <StatusBadge status={b.status} />
                        <span className="text-xs text-slate-500">v{b.version_name} ({b.version_code})</span>
                      </div>
                      <span className="text-xs text-slate-400">{new Date(b.created_at).toLocaleString()}</span>
                    </div>
                    {ACTIVE.includes(b.status) && (
                      <div className="mt-2 text-xs text-blue-600 flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        {b.queue_position && b.status === "queued" ? `Position ${b.queue_position} in queue` : (STAGE_LABEL[b.stage] || b.stage)}
                      </div>
                    )}
                    {b.status === "failed" && (
                      <div className="mt-2 text-xs text-red-600 space-y-1">
                        <div>{b.error || "Build failed"}</div>
                        {b.log_tail ? <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap rounded bg-red-100/70 p-2 text-[10px] text-red-800 font-mono">{b.log_tail}</pre> : null}
                      </div>
                    )}
                    {b.status === "success" && (
                      <div className="mt-2 flex gap-2">
                        <a href={`${BACKEND}/api/merchant/app-builder/builds/${b.id}/download/apk`} data-testid="builder-download-apk">
                          <Button size="sm" variant="outline"><Download className="w-3.5 h-3.5 mr-1" />APK</Button>
                        </a>
                        <a href={`${BACKEND}/api/merchant/app-builder/builds/${b.id}/download/aab`} data-testid="builder-download-aab">
                          <Button size="sm" variant="outline"><Download className="w-3.5 h-3.5 mr-1" />AAB (Play Store)</Button>
                        </a>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Home sliders */}
      <Card className="p-6 space-y-4" data-testid="builder-sliders">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-display font-bold text-lg text-slate-900">
            <Images className="w-5 h-5 text-blue-600" /> App home sliders
          </div>
          <Badge variant="outline" className="text-slate-500">{sliders.filter((s) => s?.image_url).length}/3</Badge>
        </div>
        <p className="text-sm text-slate-500 -mt-2">
          Add up to <b>3 banners</b> shown as a swipeable carousel at the top of your app&apos;s home screen.
          Changes go <b>live instantly</b> in installed apps — no rebuild needed. Wide images (16:9) look best.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {sliders.map((s, idx) => (
            <div key={idx} className="rounded-xl border border-slate-200 overflow-hidden" data-testid={`slider-slot-${idx}`}>
              <div className="relative aspect-video bg-slate-100 flex items-center justify-center">
                {s?.image_url ? (
                  <img src={mediaUrl(s.image_url)} alt={`Banner ${idx + 1}`} className="w-full h-full object-cover" />
                ) : (
                  <div className="text-slate-400 text-sm flex flex-col items-center gap-1">
                    <Images className="w-6 h-6" /> No image
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removeSlider(idx)}
                  className="absolute top-2 right-2 bg-white/90 hover:bg-white rounded-full p-1.5 shadow"
                  data-testid={`slider-remove-${idx}`}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </button>
              </div>
              <div className="p-3 space-y-2">
                <input
                  ref={slideRefs[idx]}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/avif"
                  className="hidden"
                  onChange={(e) => onSliderFile(idx, e)}
                  data-testid={`slider-file-${idx}`}
                />
                <Button
                  type="button" variant="outline" size="sm" className="w-full"
                  onClick={() => slideRefs[idx].current?.click()}
                  disabled={uploadingIdx === idx}
                  data-testid={`slider-upload-${idx}`}
                >
                  {uploadingIdx === idx
                    ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Uploading…</>
                    : <><UploadCloud className="w-4 h-4 mr-1" />{s?.image_url ? "Replace image" : "Upload image"}</>}
                </Button>

                <div>
                  <Label className="text-xs text-slate-500">On tap, open</Label>
                  <select
                    value={s?.link_type || "none"}
                    onChange={(e) => setSliderField(idx, { link_type: e.target.value, link_value: "" })}
                    className="mt-1 w-full h-9 rounded-md border border-slate-300 bg-white px-2 text-sm"
                    data-testid={`slider-linktype-${idx}`}
                  >
                    <option value="none">Nothing (banner only)</option>
                    <option value="category">A category</option>
                    <option value="product">A product</option>
                  </select>
                </div>

                {s?.link_type === "category" && (
                  <select
                    value={s?.link_value || ""}
                    onChange={(e) => setSliderField(idx, { link_value: e.target.value })}
                    className="w-full h-9 rounded-md border border-slate-300 bg-white px-2 text-sm"
                    data-testid={`slider-category-${idx}`}
                  >
                    <option value="">Select a category…</option>
                    {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                  </select>
                )}
                {s?.link_type === "product" && (
                  <select
                    value={s?.link_value || ""}
                    onChange={(e) => setSliderField(idx, { link_value: e.target.value })}
                    className="w-full h-9 rounded-md border border-slate-300 bg-white px-2 text-sm"
                    data-testid={`slider-product-${idx}`}
                  >
                    <option value="">Select a product…</option>
                    {prods.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
                  </select>
                )}
              </div>
            </div>
          ))}

          {sliders.length < 3 && (
            <button
              type="button"
              onClick={addSliderSlot}
              className="rounded-xl border-2 border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50/40 transition-colors flex flex-col items-center justify-center gap-2 text-slate-500 min-h-[180px]"
              data-testid="slider-add"
            >
              <UploadCloud className="w-7 h-7" />
              <span className="text-sm font-medium">Add banner</span>
            </button>
          )}
        </div>

        <Button onClick={saveSliders} disabled={sliderSaving} className="bg-slate-900 hover:bg-slate-800" data-testid="slider-save">
          {sliderSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : "Save sliders"}
        </Button>
      </Card>
    </div>
  );
}
